/**
 * Audience to TreeNode Converter
 *
 * Конвертирует Audience и Actor данные в структуру дерева для rc-tree
 */

import type { Audience, Actor } from '@segmently/schema-models/entities';
import type { AudienceTreeNode } from './types';

/**
 * Список субколлекций Actor для отображения
 */
const ACTOR_SUBCOLLECTIONS = [
  'coreJobs',
  'contexts',
  'fears',
  'bigJobs',
  'subtasks',
  'competitors',
  'habits',
] as const;

/**
 * Форматирует название поля для отображения
 */
function formatFieldName(fieldName: string): string {
  // coreJobs -> Core Jobs
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Конвертирует Audience в узел дерева
 */
export function audienceToTreeNode(audience: Audience, highlightEmpty = false): AudienceTreeNode {
  const children: AudienceTreeNode[] = [];

  const hasActors = audience.actors && audience.actors.length > 0;
  const dimmed = highlightEmpty && !hasActors;

  // Добавляем узел audience (теперь checkable)
  const audienceNode: AudienceTreeNode = {
    key: `aud-${audience.id}`,
    title: dimmed
      ? `${audience.name || `Audience ${audience.id}`} (no actors)`
      : (audience.name || `Audience ${audience.id}`),
    nodeType: 'audience' as const,
    audienceId: audience.id,
    checkable: true, // Можно выбрать целую audience
    style: dimmed ? { opacity: 0.5 } : undefined,
    children: [],
  };

  // Добавляем поля audience
  const audienceFields = ['name', 'description', 'goalToBeDone'];
  audienceFields.forEach((fieldName) => {
    const fieldValue = audience[fieldName as keyof Audience];
    if (fieldValue !== undefined && fieldValue !== null) {
      audienceNode.children!.push({
        key: `aud-${audience.id}-${fieldName}`,
        title: `${formatFieldName(fieldName)}: ${String(fieldValue).substring(0, 50)}`,
        nodeType: 'field' as const,
        audienceId: audience.id,
        fieldName,
        checkable: true,
      });
    }
  });

  // Добавляем actors напрямую (без промежуточной папки)
  if (audience.actors && audience.actors.length > 0) {
    audience.actors.forEach((actor) => {
      audienceNode.children!.push(actorToTreeNode(actor, audience.id!));
    });
  }

  return audienceNode;
}

/**
 * Конвертирует Actor в узел дерева
 */
function actorToTreeNode(actor: Actor, audienceId: string): AudienceTreeNode {
  const children: AudienceTreeNode[] = [];

  // Добавляем базовые поля actor
  const basicFields = ['name', 'description', 'weight'];
  basicFields.forEach((fieldName) => {
    const fieldValue = actor[fieldName as keyof Actor];
    if (fieldValue !== undefined && fieldValue !== null) {
      children.push({
        key: `act-${actor.id}-${fieldName}`,
        title: `${formatFieldName(fieldName)}: ${String(fieldValue).substring(0, 50)}`,
        nodeType: 'field' as const,
        audienceId,
        actorId: actor.id,
        fieldName,
        checkable: true,
      });
    }
  });

  // Добавляем субколлекции (coreJobs, fears, contexts и т.д.)
  ACTOR_SUBCOLLECTIONS.forEach((subcollectionName) => {
    const items = actor[subcollectionName] as any[];

    // Показываем только если массив не пустой
    if (Array.isArray(items) && items.length > 0) {
      children.push({
        key: `act-${actor.id}-${subcollectionName}`,
        title: `${formatFieldName(subcollectionName)} (${items.length})`,
        nodeType: 'subcollection' as const,
        audienceId,
        actorId: actor.id,
        subcollectionName, // Добавляем subcollectionName для path conversion
        checkable: true, // Теперь можно выбрать всю subcollection
        children: items.map((item, index) => ({
          key: `act-${actor.id}-${subcollectionName}-${index}`,
          title: item.description || `Item ${index + 1}`,
          nodeType: 'arrayItem' as const,
          audienceId,
          actorId: actor.id,
          fieldName: subcollectionName,
          arrayIndex: index,
          itemFirebaseId: item.id, // Store Firebase ID for AI path matching
          checkable: true,
        })),
      });
    }
  });

  // Создаем узел actor (теперь checkable)
  return {
    key: `act-${actor.id}`,
    title: actor.name || `Actor ${actor.id}`,
    nodeType: 'actor' as const,
    audienceId,
    actorId: actor.id,
    checkable: true, // Можно выбрать целого actor
    children,
  };
}
